import React, { Fragment, useMemo } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { SelectorIcon } from '@heroicons/react/solid';
import classNames from 'classnames';

type MultiSelectProps = {
  selected: (number | string)[];
  options:
    | (number | string)[]
    | { label: string; val: string; disabled?: boolean; isHeader?: boolean }[];
  setSelected: (value: (number | string)[]) => void;
  label?: string;
  placeholder?: string;
  id?: string;
  style?: any;
  showSelectedValues?: boolean;
  maxValuesAllowed?: number;
  emptyMessage?: string;
  onOpen?: () => void;
  disabled?: boolean;
};

export const MultiSelect = ({
  options,
  selected,
  setSelected,
  label,
  placeholder,
  id,
  style,
  showSelectedValues = true,
  maxValuesAllowed = 100,
  emptyMessage = 'No options available.',
  onOpen,
  disabled = false,
}: MultiSelectProps) => {
  const selectedValuesJoined = useMemo(() => {
    if (!options.length) return '';

    const isObject = typeof options[0] === 'object';

    return selected
      .map((s) => {
        if (isObject) {
          return (
            options as {
              label: string;
              val: string;
            }[]
          ).find((i) => i.val === s)?.label;
        } else {
          return s;
        }
      })
      .filter(Boolean)
      .join(', ');
  }, [selected, options]);

  return (
    <Listbox
      value={selected}
      onChange={(val: any) => {
        if (selected.includes(val)) {
          setSelected(selected.filter((v) => v !== val));
        } else {
          setSelected([...selected, val]);
        }
      }}
      as="div"
      id={id || 'select'}
      disabled={disabled}
    >
      {({ open }) => (
        <>
          {label && (
            <Listbox.Label className="block text-sm font-medium text-gray-700">
              {label}
            </Listbox.Label>
          )}
          <div className="mt-1 relative">
            <Listbox.Button
              disabled={disabled}
              onClick={() => {
                if (!open) onOpen?.();
              }}
              style={{ minHeight: 38, ...style }}
              className={classNames(
                'relative w-full border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm',
                disabled
                  ? 'cursor-not-allowed bg-gray-100 opacity-70'
                  : 'bg-white cursor-default',
              )}
            >
              {placeholder?.length && !selected.length && (
                <span className="text-gray-700">{placeholder}</span>
              )}
              {showSelectedValues && selected.length ? (
                <span className="block truncate">{selectedValuesJoined}</span>
              ) : null}
              <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <SelectorIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options
                onClick={(e: any) => {
                  e.stopPropagation();
                }}
                static
                className="absolute z-[10000] mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
              >
                {options.length === 0 && (
                  <div className="px-3 py-3 text-sm text-gray-500">
                    {emptyMessage}
                  </div>
                )}
                {options.map((option) => {
                  const label =
                    typeof option === 'object' ? option.label : option;
                  const isOptionDisabled =
                    typeof option === 'object' ? option.disabled : false;
                  const isHeader =
                    typeof option === 'object' ? option.isHeader : false;
                  const val = typeof option === 'object' ? option.val : option;
                  const isSelected = selected.includes(val);
                  const isDisabled =
                    disabled ||
                    (selected.length === maxValuesAllowed &&
                      !selected.includes(val)) ||
                    isOptionDisabled;

                  if (isHeader) {
                    return (
                      <div
                        key={val as string}
                        className="px-3 py-2 text-xs font-bold text-gray-500 uppercase bg-gray-50 tracking-wider"
                      >
                        {label}
                      </div>
                    );
                  }

                  return (
                    <Listbox.Option
                      key={val as string}
                      disabled={isDisabled}
                      className={({ active }) =>
                        classNames(
                          'cursor-default select-none relative py-2 pl-3 pr-9',
                          active && !isSelected && 'bg-indigo-600',
                          isSelected && !active && 'bg-blue-100',
                          active && isSelected && 'bg-indigo-600',
                          isDisabled && 'opacity-25',
                        )
                      }
                      value={val}
                    >
                      {({ active }) => (
                        <div className="relative flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id={val as string}
                              name={val as string}
                              type="checkbox"
                              className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                              checked={selected.includes(val)}
                              readOnly
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label
                              className={classNames(
                                'block truncate',
                                active && !isSelected && 'text-white',
                                isSelected &&
                                  !active &&
                                  'font-semibold text-gray-900',
                                active &&
                                  isSelected &&
                                  'font-semibold text-white',
                              )}
                            >
                              {label}
                            </label>
                          </div>
                        </div>
                      )}
                    </Listbox.Option>
                  );
                })}
              </Listbox.Options>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  );
};
